import React, { useState, useEffect } from 'react';
import { useAccessControl, Client } from '@/hooks/useAccessControl';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Key, ClipboardCopy, Plus, Users, Search, Code2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeDialog, NativeDialogContent, NativeDialogDescription, NativeDialogHeader, NativeDialogTitle, NativeDialogFooter } from '@/components/ui/native-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import InlineClientForm from './InlineClientForm';
import { Label } from '@/components/ui/label';

const ClientList: React.FC = () => {
  const { clients, updateClient, deleteClient, createAccessCode, createClient } = useAccessControl();
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [accessCodeClient, setAccessCodeClient] = useState<Client | null>(null);
  const [newAccessCode, setNewAccessCode] = useState<string | null>(null);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editName, setEditName] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [selectedClientForEmbed, setSelectedClientForEmbed] = useState<Client | null>(null);

  // Filter out the default client and apply search
  const displayClients = clients
    .filter(client => client.id !== '00000000-0000-0000-0000-000000000000')
    .filter(client => client.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // Reset states when dialog closes
  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditName('');
    setDeleteConfirmName('');
    setShowDeleteConfirm(false);
    setEditingClient(null);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setEditName(client.name);
    setShowEditDialog(true);
  };

  const handleUpdateClient = async () => {
    if (!editingClient || !editName.trim() || isEditing) return;
    
    setIsEditing(true);
    try {
      const success = await updateClient(editingClient.id, editName);
      if (success) {
        handleEditDialogClose();
        toast({
          title: 'Success',
          description: 'Client name updated successfully',
        });
      }
    } finally {
      setIsEditing(false);
    }
  };
  
  const handleAddClient = () => {
    setShowAddForm(true);
  };
  
  const handleCancelAdd = () => {
    setShowAddForm(false);
  };
  
  const handleCreateClient = async (name: string) => {
    if (!name.trim() || isCreating) return;
    
    setIsCreating(true);
    try {
      await createClient(name);
      setShowAddForm(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!editingClient || isDeleting || deleteConfirmName !== editingClient.name) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteClient(editingClient.id);
      if (success) {
        handleEditDialogClose();
        toast({
          title: 'Success',
          description: 'Client deleted successfully',
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateAccessCode = async (client: Client) => {
    setAccessCodeClient(client);
    setNewAccessCode(null);
    setOpenAccessDialog(true);
  };

  const generateCode = async (isAdmin: boolean) => {
    const clientId = isAdmin ? null : accessCodeClient?.id;
    const code = await createAccessCode(clientId, isAdmin);
    setNewAccessCode(code);
  };

  const copyToClipboard = (text: string, type: 'code' | 'access-code' = 'code') => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: type === 'access-code' ? 'Access code copied to clipboard' : 'Copied to clipboard',
    });
  };

  const isDefaultClient = (client: Client) => client.id === '00000000-0000-0000-0000-000000000000';

  const handleShowEmbedInstructions = (client: Client) => {
    setSelectedClientForEmbed(client);
    setShowEmbedDialog(true);
  };

  return (
    <Card className="mb-4 bg-white border-2 rounded-lg overflow-hidden border-slate-800">
      <div className="p-4 bg-slate-800 text-white">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            <span>Clients</span>
          </CardTitle>
          <Button 
            onClick={handleAddClient}
            size="sm"
            className="bg-white text-slate-800 hover:bg-slate-100"
          >
            <Plus size={16} className="mr-1" />
            Add Client
          </Button>
        </div>
      </div>
      
      <CardContent className="p-0">
        {/* Add Client Form */}
        {showAddForm && (
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <InlineClientForm
              onSubmit={handleCreateClient}
              onCancel={handleCancelAdd}
            />
          </div>
        )}
        
        {/* Edit Client Dialog */}
        <NativeDialog open={showEditDialog} size="sm" onOpenChange={handleEditDialogClose}>
          <NativeDialogContent>
            <NativeDialogHeader>
              <NativeDialogTitle>Edit Client</NativeDialogTitle>
              <NativeDialogDescription>
                Make changes to {editingClient?.name}
              </NativeDialogDescription>
            </NativeDialogHeader>

            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter client name"
                />
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  onClick={handleUpdateClient}
                  disabled={isEditing || !editName.trim()}
                  className="w-full"
                >
                  {isEditing ? 'Saving...' : 'Save Changes'}
                </Button>

                {!showDeleteConfirm ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full"
                  >
                    Delete Client
                  </Button>
                ) : (
                  <div className="space-y-3 border-t pt-3">
                    <div className="space-y-2">
                      <Label htmlFor="confirmDelete" className="text-red-500">
                        Type "{editingClient?.name}" to confirm deletion
                      </Label>
                      <Input
                        id="confirmDelete"
                        value={deleteConfirmName}
                        onChange={(e) => setDeleteConfirmName(e.target.value)}
                        placeholder="Enter client name"
                        className="border-red-200"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmName('');
                        }}
                        className="w-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleDeleteClient}
                        disabled={isDeleting || deleteConfirmName !== editingClient?.name}
                        className="w-full"
                      >
                        {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </NativeDialogContent>
        </NativeDialog>

        {/* Search Bar */}
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-slate-50"
            />
          </div>
        </div>
        
        {/* Client List */}
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-slate-50/50">
              <tr>
                <th scope="col" className="px-4 py-3">Name</th>
                <th scope="col" className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayClients.map((client) => (
                <tr key={client.id} className="border-b hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">{client.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditClient(client)}
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleGenerateAccessCode(client)}
                      >
                        <Key size={14} className="mr-1" />
                        Access Code
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleShowEmbedInstructions(client)}
                      >
                        <Code2 size={14} className="mr-1" />
                        Embed
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Access Code Dialog */}
      <NativeDialog open={openAccessDialog} size="sm" onOpenChange={setOpenAccessDialog}>
        <NativeDialogContent>
          <NativeDialogHeader>
            <NativeDialogTitle>Generate Access Code</NativeDialogTitle>
            <NativeDialogDescription>
              Generate an access code for {accessCodeClient?.name}
            </NativeDialogDescription>
          </NativeDialogHeader>
          
          {newAccessCode ? (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <code className="text-sm">{newAccessCode}</code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(newAccessCode, 'access-code')}
                >
                  <ClipboardCopy size={14} className="mr-1" />
                  Copy
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Share this code with the client. It can only be used once and expires in 24 hours.
              </p>
            </div>
          ) : (
            <NativeDialogFooter>
              <Button
                onClick={() => generateCode(false)}
                className="w-full"
              >
                Generate Client Access Code
              </Button>
            </NativeDialogFooter>
          )}
        </NativeDialogContent>
      </NativeDialog>

      {/* Embed Instructions Dialog */}
      <NativeDialog open={showEmbedDialog} size="4xl" onOpenChange={setShowEmbedDialog}>
        <NativeDialogContent>
          <NativeDialogHeader>
            <NativeDialogTitle>Embed Instructions</NativeDialogTitle>
            <NativeDialogDescription>
              Follow these steps to embed the release tracking system for {selectedClientForEmbed?.name}
            </NativeDialogDescription>
          </NativeDialogHeader>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-2">1. Add a div with these values in Webflow:</h3>
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="mb-4 p-3 bg-white rounded border border-slate-200">
                  <code className="text-sm font-mono text-slate-600">
                    {'<div id="webflow-versioning-system" data-client-id="' + selectedClientForEmbed?.id + '"></div>'}
                  </code>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Div ID:</span>
                    <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">webflow-versioning-system</code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard('webflow-versioning-system')}
                    >
                      <ClipboardCopy size={14} className="mr-1" />
                      Copy
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Data Attribute:</span>
                    <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">data-client-id</code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard('data-client-id')}
                    >
                      <ClipboardCopy size={14} className="mr-1" />
                      Copy
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Client ID:</span>
                    <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{selectedClientForEmbed?.id}</code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(selectedClientForEmbed?.id || '')}
                    >
                      <ClipboardCopy size={14} className="mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2">2. Add the following scripts before the closing body tag:</h3>
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Required Scripts:</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard('<!-- React and ReactDOM -->\n<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>\n<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>\n\n<!-- Versioning System -->\n<script src="https://webflow-releases.netlify.app/webflow-loader.js"></script>')}
                  >
                    <ClipboardCopy size={14} className="mr-1" />
                    Copy Scripts
                  </Button>
                </div>
                <code className="block text-sm font-mono whitespace-pre bg-white p-3 rounded border border-slate-200">{'<!-- React and ReactDOM -->\n<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>\n<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>\n\n<!-- Versioning System -->\n<script src="https://webflow-releases.netlify.app/webflow-loader.js"></script>'}</code>
              </div>
            </div>
          </div>

          <NativeDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEmbedDialog(false)}
            >
              Close
            </Button>
          </NativeDialogFooter>
        </NativeDialogContent>
      </NativeDialog>
    </Card>
  );
};

export default ClientList;
